import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TRAVEL_API_PATH = '/api/travel';

export type ErrorResponse = { error: string; message?: string; [k: string]: any };

export type ActivitySummary = {
  id: string;
  provider?: string;
  name: string;
  description: string | null;
  image_path: string | null;
  city_tour_type: string | null;
  ticketPrices?: {
    adultPrice: number;
    childPrice: number;
    currency: string;
  };
};

export type ActivityListResponse = {
  items: ActivitySummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  priceJobId?: string;
};

export type ActivityPrice = {
  amount: number;
  discount?: number;
  contractId?: number;
  sortOrder?: number;
  currency?: string;
};

export type ActivityPricePollResponse =
  | { status: 'PENDING' }
  | { status: 'FAILED'; error?: string }
  | { status: 'SUCCESS'; currency: 'INR'; prices: Record<string, ActivityPrice> };

export type ActivityEstimate = {
  status: 'AVAILABLE' | 'ADVISORY';
  amount?: number;
  totalAmount?: number;
  ticketTotal?: number;
  transferTotal?: number;
  currency?: string;
  mode?: 'TICKET_ONLY' | 'TRANSFER_ONLY' | 'TICKET_AND_TRANSFER';
  selectedCabType?: string | null;
  cabOptions?: Array<{
    cabType: string;
    name: string;
    passengerCapacity: number;
    transferPrice: number;
  }>;
  error?: string;
};

export type ActivityEstimateResponse = {
  items: Record<string, ActivityEstimate>;
  currency: string;
  provisional: boolean;
};

export type ActivityCity = {
  cityId: number;
  cityName: string | null;
  countryId: number;
  countryName: string | null;
};

export type ActivitySearchTravellerMix = {
  adults: number;
  children: number;
  infants: number;
};

export type ActivityTourSearchItem = {
  activityRef: string;
  provider: 'RAYNA';
  tourId: number;
  contractId: number;
  name: string;
  description: string | null;
  imageUrl: string | null;
  duration: string | null;
  optionCount: number;
  pricePreview: { amount: number; currency: string } | null;
  priceStatus: 'AVAILABLE' | 'UNAVAILABLE';
};

export type ActivityTourSearchResponse = {
  cityId: number;
  travelDate: string;
  travellers: ActivitySearchTravellerMix;
  currency: string;
  previewError?: string;
  items: ActivityTourSearchItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

export type ActivityTransferQuote = {
  transferId: number;
  transferName: string;
  startTime: string | null;
  isSlot: boolean;
  adultPrice: number;
  childPrice: number;
  infantPrice: number;
  totalPrice: number;
  currency: string;
};

export type ActivityOptionQuote = {
  tourOptionId: number;
  optionName: string;
  status: 'AVAILABLE' | 'UNAVAILABLE';
  transfers: ActivityTransferQuote[];
};

export type ActivityOptionSearchResponse = {
  jobId: string;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  activityRef: string;
  tourId: number;
  contractId: number;
  travelDate: string;
  travellers: ActivitySearchTravellerMix;
  options: ActivityOptionQuote[];
  error?: string;
  quotedAt?: string;
};

export type FetchActivitiesParams = {
  page?: number;
  limit?: number;
  pageSize?: number;
  name?: string;
  city?: string;
  cityId?: number;
  country?: string;
  countryId?: number;
  travelDate?: string;
};

const toNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toNullableString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  return String(value);
};

const buildError = async (response: Response, fallback: string) => {
  try {
    const body = await response.json();
    const text = body?.message || body?.error;
    if (text) return new Error(String(text));
  } catch {
    // fall through to status-based message
  }
  return new Error(`${fallback} (${response.status} ${response.statusText})`);
};

const normalizeSummary = (item: any): ActivitySummary => ({
  id: String(item?.id ?? item?.tourId ?? item?.tour_id ?? '').trim(),
  ...(item?.provider ? { provider: String(item.provider) } : {}),
  name: String(item?.name || ''),
  description: toNullableString(item?.description),
  image_path: toNullableString(
    item?.image_path
    ?? item?.imagePath
    ?? item?.image_url
    ?? item?.imageUrl
    ?? item?.picture_url
    ?? item?.pictureUrl
    ?? item?.thumbnail_url
    ?? item?.thumbnailUrl
    ?? item?.thumbnail
    ?? item?.image
  ),
  city_tour_type: toNullableString(item?.city_tour_type ?? item?.cityTourType),
  ...(item?.ticketPrices || item?.ticket_prices
    ? {
      ticketPrices: {
        adultPrice: toNumber(item?.ticketPrices?.adultPrice ?? item?.ticket_prices?.adult_price),
        childPrice: toNumber(item?.ticketPrices?.childPrice ?? item?.ticket_prices?.child_price),
        currency: String(item?.ticketPrices?.currency ?? item?.ticket_prices?.currency ?? 'INR'),
      },
    }
    : {}),
});

const normalizeCity = (item: any): ActivityCity => ({
  cityId: toNumber(item?.cityId ?? item?.city_id, 0),
  cityName: toNullableString(item?.cityName ?? item?.city_name),
  countryId: toNumber(item?.countryId ?? item?.country_id, 0),
  countryName: toNullableString(item?.countryName ?? item?.country_name),
});

const normalizePrice = (price: any): ActivityPrice => ({
  amount: toNumber(price?.amount, 0),
  discount: price?.discount === undefined || price?.discount === null ? undefined : toNumber(price?.discount, 0),
  contractId: price?.contractId === undefined || price?.contractId === null ? undefined : toNumber(price?.contractId, 0),
  sortOrder: price?.sortOrder === undefined || price?.sortOrder === null ? undefined : toNumber(price?.sortOrder, 0),
  currency: price?.currency ? String(price.currency) : undefined,
});

export const fetchActivities = async (params: FetchActivitiesParams = {}): Promise<ActivityListResponse> => {
  const searchParams = new URLSearchParams();
  if (params.page !== undefined) searchParams.set('page', String(params.page));
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.pageSize !== undefined) searchParams.set('pageSize', String(params.pageSize));
  if (params.name) searchParams.set('name', params.name);
  if (params.city) searchParams.set('city', params.city);
  if (params.cityId !== undefined) searchParams.set('cityId', String(params.cityId));
  if (params.country) searchParams.set('country', params.country);
  if (params.countryId !== undefined) searchParams.set('countryId', String(params.countryId));
  if (params.travelDate) searchParams.set('travelDate', params.travelDate);

  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities?${searchParams.toString()}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  if (!response.ok) throw await buildError(response, 'Failed to fetch activities');

  const data = await response.json();
  // The direct travel service returns this shape at the top level. Some deployed
  // gateways wrap it in `data`, so accept both without losing the price job.
  const payload = data?.data && typeof data.data === 'object' ? data.data : data;
  const rawItems = Array.isArray(payload?.items) ? payload.items : [];
  const paginationSource = payload?.pagination || {};

  const result: ActivityListResponse = {
    items: rawItems.map(normalizeSummary).filter((item: ActivitySummary) => Boolean(item.id)),
    pagination: {
      page: toNumber(paginationSource.page ?? payload?.page, 1),
      limit: toNumber(paginationSource.limit ?? payload?.limit ?? payload?.pageSize, 50),
      total: toNumber(paginationSource.total ?? payload?.total, rawItems.length),
      totalPages: toNumber(paginationSource.totalPages ?? payload?.totalPages, 1),
    },
  };

  const priceJobId = payload?.priceJobId ?? payload?.price_job_id ?? payload?.jobId ?? payload?.job_id;
  if (priceJobId) result.priceJobId = String(priceJobId);

  return result;
};

export const searchActivityTours = async (input: {
  cityId: number;
  travelDate: string;
  travellers: ActivitySearchTravellerMix;
  name?: string;
  page?: number;
  limit?: number;
}): Promise<ActivityTourSearchResponse> => {
  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities/search`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({
      cityId: input.cityId,
      ...(input.name?.trim() ? { name: input.name.trim() } : {}),
      travelDate: input.travelDate,
      adults: input.travellers.adults,
      children: input.travellers.children,
      infants: input.travellers.infants,
      page: input.page || 1,
      limit: input.limit || 20,
    }),
  });
  if (!response.ok) throw await buildError(response, 'Failed to search activity tours');
  return response.json();
};

export const startActivityOptionSearch = async (input: {
  activityRef: string;
  contractId: number;
  travelDate: string;
  travellers: ActivitySearchTravellerMix;
}): Promise<ActivityOptionSearchResponse> => {
  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities/${encodeURIComponent(input.activityRef)}/options/search`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({
      contractId: input.contractId,
      travelDate: input.travelDate,
      adults: input.travellers.adults,
      children: input.travellers.children,
      infants: input.travellers.infants,
    }),
  });
  if (!response.ok) throw await buildError(response, 'Failed to load activity options');
  return response.json();
};

export const fetchActivityOptionSearch = async (jobId: string): Promise<ActivityOptionSearchResponse> => {
  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities/options-search/${encodeURIComponent(jobId)}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });
  if (!response.ok) throw await buildError(response, 'Failed to refresh activity options');
  return response.json();
};

export const fetchActivityPrices = async (priceJobId: string): Promise<ActivityPricePollResponse> => {
  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities/prices/${encodeURIComponent(priceJobId)}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  if (!response.ok) throw await buildError(response, 'Failed to fetch activity prices');

  const data = await response.json();
  const payload = data?.data && typeof data.data === 'object' ? data.data : data;
  const status = String(payload?.status || '').toUpperCase();

  if (status === 'PENDING') return { status: 'PENDING' };

  if (status === 'SUCCESS') {
    const rawPrices = payload?.prices && typeof payload.prices === 'object' ? payload.prices : {};
    const prices: Record<string, ActivityPrice> = {};
    Object.entries(rawPrices).forEach(([key, value]) => {
      prices[key] = normalizePrice(value);
    });
    return {
      status: 'SUCCESS',
      currency: 'INR',
      prices,
    };
  }

  return {
    status: 'FAILED',
    error: payload?.error ? String(payload.error) : undefined,
  };
};

/**
 * Retrieves a traveller-aware price for the activity picker. This is the
 * authoritative quote path: it includes an OTA Prime transfer fare where one
 * is configured, while retaining Rayna's live supplier pricing.
 */
export const estimateActivities = async (input: {
  activityRefs: Array<string | { activityRef: string; cabType?: string }>;
  travelDate: string;
  adults: number;
  children?: number;
  infants?: number;
}): Promise<ActivityEstimateResponse> => {
  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities/estimate`, {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify({
      activities: input.activityRefs.map((activity) => typeof activity === 'string'
        ? { activityRef: activity }
        : { activityRef: activity.activityRef, ...(activity.cabType ? { cabType: activity.cabType } : {}) }),
      travelDate: input.travelDate,
      adults: input.adults,
      children: input.children || 0,
      infants: input.infants || 0,
    }),
  });

  if (!response.ok) throw await buildError(response, 'Failed to estimate activities');
  const data = await response.json();
  const payload = data?.data && typeof data.data === 'object' ? data.data : data;
  const rawItems = payload?.items && typeof payload.items === 'object' ? payload.items : {};
  const items: Record<string, ActivityEstimate> = {};
  Object.entries(rawItems).forEach(([activityRef, item]: [string, any]) => {
    items[activityRef] = {
      status: String(item?.status || '').toUpperCase() === 'AVAILABLE' ? 'AVAILABLE' : 'ADVISORY',
      amount: item?.amount === undefined ? undefined : toNumber(item.amount),
      totalAmount: item?.totalAmount === undefined ? undefined : toNumber(item.totalAmount),
      ticketTotal: item?.ticketTotal === undefined ? undefined : toNumber(item.ticketTotal),
      transferTotal: item?.transferTotal === undefined ? undefined : toNumber(item.transferTotal),
      currency: item?.currency ? String(item.currency) : undefined,
      mode: item?.mode,
      selectedCabType: item?.selectedCabType ? String(item.selectedCabType) : null,
      cabOptions: Array.isArray(item?.cabOptions) ? item.cabOptions.map((option: any) => ({
        cabType: String(option?.cabType || ''),
        name: String(option?.name || option?.cabType || ''),
        passengerCapacity: toNumber(option?.passengerCapacity),
        transferPrice: toNumber(option?.transferPrice),
      })).filter((option: { cabType: string }) => Boolean(option.cabType)) : undefined,
      error: item?.error ? String(item.error) : undefined,
    };
  });
  return { items, currency: String(payload?.currency || 'INR'), provisional: Boolean(payload?.provisional) };
};

export const fetchActivityCities = async (countryId?: number): Promise<ActivityCity[]> => {
  const searchParams = new URLSearchParams();
  if (countryId !== undefined) searchParams.set('countryId', String(countryId));
  const suffix = searchParams.toString();

  const response = await fetch(`${API_BASE_URL}${TRAVEL_API_PATH}/activities/cities${suffix ? `?${suffix}` : ''}`, {
    method: 'GET',
    headers: getApiHeaders('json'),
  });

  if (!response.ok) throw await buildError(response, 'Failed to fetch activity cities');

  const data = await response.json();
  const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  return items.map(normalizeCity).filter((item: ActivityCity) => item.cityId > 0);
};
